import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { validatePromoCode } from '../services/api';
import { Trash2, ArrowLeft, ShoppingBag, Check, Tag, X, Loader2 } from 'lucide-react';

const CheckoutPage = () => {
  const { cartItems, totalPrice, totalItems, removeFromCart, updateQty, clearCart } = useCart();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', zip: '', country: 'Romania',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  const discount = appliedPromo ? appliedPromo.discount : 0;
  const finalTotal = Math.max(0, totalPrice - discount);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoError('');
    setPromoLoading(true);
    try {
      const result = await validatePromoCode(promoInput.trim(), totalPrice);
      setAppliedPromo(result);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Cod invalid');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName || !form.lastName || !form.email || !form.address || !form.city) {
      return setError('Completați toate câmpurile obligatorii.');
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setStep(3);
    clearCart();
    setAppliedPromo(null);
    setLoading(false);
  };

  if (cartItems.length === 0 && step !== 3) {
    return (
      <main className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag size={64} className="text-gray-200 mx-auto mb-4" />
          <h2 className="text-[#0a0a0a] font-black italic text-3xl mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            coșul este gol
          </h2>
          <p className="text-gray-500 mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Nu ai produse în coș. Descoperă arome delicioase!
          </p>
          <Link to="/shop" data-testid="empty-cart-shop-link" className="bg-[#0a0a0a] text-white font-black italic px-8 py-3 rounded-2xl hover:bg-[#222222] transition-colors" style={{ fontFamily: 'Nunito, sans-serif' }}>
            du-mă la shop
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 pt-20" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-[#0a0a0a] font-bold text-sm hover:opacity-70 transition-opacity" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <ArrowLeft size={16} /> înapoi la shop
          </Link>
          <h1 className="text-[#0a0a0a] font-black italic text-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {step === 1 ? 'coș de cumpărături' : step === 2 ? 'finalizare comandă' : 'comandă plasată'}
          </h1>
          {step !== 3 ? (
            <span className="text-gray-400 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {totalItems} produs{totalItems !== 1 ? 'e' : ''}
            </span>
          ) : <div />}
        </div>
      </div>

      {/* Steps indicator */}
      {step !== 3 && (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-6 py-3 flex gap-8">
            {['Coș', 'Date livrare', 'Confirmare'].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${
                    step > i + 1 ? 'bg-green-500 text-white' :
                    step === i + 1 ? 'bg-[#0a0a0a] text-white' :
                    'bg-gray-200 text-gray-400'
                  }`}
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {step > i + 1 ? <Check size={12} /> : i + 1}
                </div>
                <span className={`text-sm font-bold ${step === i + 1 ? 'text-[#0a0a0a]' : 'text-gray-400'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* STEP 3: Success */}
        {step === 3 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-500" />
            </div>
            <h2 className="text-[#0a0a0a] font-black italic text-4xl mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              comandă plasată!
            </h2>
            <p className="text-gray-600 text-lg mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Mulțumim pentru comandă! Vei primi un email de confirmare în curând.
            </p>
            <p className="text-gray-400 text-sm mb-10" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Produsele tale Seltz sunt în drum spre tine.
            </p>
            <Link to="/" className="bg-[#0a0a0a] text-white font-black italic px-12 py-4 rounded-2xl hover:bg-[#222222] transition-all hover:scale-105 shadow-lg" style={{ fontFamily: 'Nunito, sans-serif' }}>
              înapoi la home
            </Link>
          </div>
        )}

        {/* STEP 1: Cart items */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 flex flex-col gap-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl p-5 flex gap-4 shadow-sm">
                  <div className="flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center" style={{ width: '90px', height: '90px', background: 'linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 100%)' }}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[#0a0a0a] font-black italic text-base mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>{item.name}</h4>
                    <p className="text-gray-400 text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>{item.specs}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-3 py-1.5">
                        <button data-testid={`qty-minus-${item.id}`} onClick={() => updateQty(item.id, item.quantity - 1)} className="text-[#0a0a0a] font-black text-lg leading-none hover:opacity-70">-</button>
                        <span className="text-[#0a0a0a] font-black text-sm w-4 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>{item.quantity}</span>
                        <button data-testid={`qty-plus-${item.id}`} onClick={() => updateQty(item.id, item.quantity + 1)} className="text-[#0a0a0a] font-black text-lg leading-none hover:opacity-70">+</button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[#0a0a0a] font-black text-lg" style={{ fontFamily: 'Nunito, sans-serif' }}>${(item.price * item.quantity).toFixed(2)}</span>
                        <button data-testid={`remove-item-${item.id}`} onClick={() => removeFromCart(item.id)} className="text-gray-300 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-2xl p-6 shadow-sm h-fit">
              <h3 className="text-[#0a0a0a] font-black italic text-lg mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>sumar comandă</h3>
              <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 mb-4">
                <div className="flex justify-between text-sm text-gray-600" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <span>Subtotal ({totalItems} produse)</span><span>${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <span>Livrare</span><span className="text-green-600 font-bold">Gratuită</span>
                </div>
                {appliedPromo && (
                  <div className="flex justify-between text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    <span className="text-green-600 font-bold flex items-center gap-1">
                      <Tag size={12} /> {appliedPromo.code}
                    </span>
                    <span className="text-green-600 font-bold">-${discount.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Promo code input */}
              <div className="mb-4">
                {appliedPromo ? (
                  <div data-testid="applied-promo-badge" className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
                    <span className="text-green-700 font-bold text-sm flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      <Tag size={14} /> {appliedPromo.code} aplicat
                      {appliedPromo.type === 'percentage' ? ` (-${appliedPromo.value}%)` : ` (-$${appliedPromo.value})`}
                    </span>
                    <button onClick={handleRemovePromo} className="text-green-600 hover:text-red-500 transition-colors"><X size={16} /></button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <input
                        data-testid="promo-code-input"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        placeholder="Cod promoțional"
                        className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors uppercase"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      />
                      <button
                        data-testid="apply-promo-btn"
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoInput.trim()}
                        className="bg-[#0a0a0a] text-white font-black italic text-xs px-4 py-2.5 rounded-xl hover:bg-[#222222] transition-colors disabled:opacity-50 flex items-center gap-1"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      >
                        {promoLoading ? <Loader2 size={14} className="animate-spin" /> : 'aplică'}
                      </button>
                    </div>
                    {promoError && (
                      <p data-testid="promo-error" className="text-red-500 text-xs font-bold mt-1.5" style={{ fontFamily: 'Nunito, sans-serif' }}>{promoError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-6">
                <span className="text-[#0a0a0a] font-black text-base" style={{ fontFamily: 'Nunito, sans-serif' }}>Total</span>
                <span data-testid="checkout-total" className="text-[#0a0a0a] font-black text-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>${finalTotal.toFixed(2)}</span>
              </div>
              <button data-testid="checkout-continue-btn" onClick={() => setStep(2)} className="w-full bg-[#0a0a0a] text-white font-black italic text-base py-4 rounded-2xl hover:bg-[#222222] transition-all hover:scale-105 shadow-lg" style={{ fontFamily: 'Nunito, sans-serif' }}>
                continuă
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Delivery details */}
        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <form onSubmit={handleOrder} className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-[#0a0a0a] font-black italic text-lg mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>date de livrare</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { name: 'firstName', label: 'Prenume', placeholder: 'Ion' },
                  { name: 'lastName', label: 'Nume', placeholder: 'Popescu' },
                  { name: 'email', label: 'Email', placeholder: 'ion@email.com' },
                  { name: 'phone', label: 'Telefon', placeholder: '+40 7xx xxx xxx' },
                ].map((f) => (
                  <div key={f.name} className="flex flex-col gap-1">
                    <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder}
                      className="border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors"
                      style={{ fontFamily: 'Nunito, sans-serif' }} />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-4">
                {[
                  { name: 'address', label: 'Adresă', placeholder: 'Strada, nr...' },
                  { name: 'city', label: 'Oraș', placeholder: 'București' },
                  { name: 'zip', label: 'Cod Poștal', placeholder: '010000' },
                ].map((f) => (
                  <div key={f.name} className="flex flex-col gap-1">
                    <label className="text-[#0a0a0a] font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'Nunito, sans-serif' }}>{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder}
                      className="border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#0a0a0a] transition-colors"
                      style={{ fontFamily: 'Nunito, sans-serif' }} />
                  </div>
                ))}
              </div>
              {error && <p className="text-red-500 text-sm font-bold mt-4" style={{ fontFamily: 'Nunito, sans-serif' }}>{error}</p>}
              <button type="submit" disabled={loading} data-testid="place-order-btn"
                className="w-full bg-[#0a0a0a] text-white font-black italic text-base py-4 rounded-2xl hover:bg-[#222222] transition-all hover:scale-105 shadow-lg mt-6 disabled:opacity-70"
                style={{ fontFamily: 'Nunito, sans-serif' }}>
                {loading ? 'se procesează...' : 'plasează comanda'}
              </button>
            </form>

            {/* Order summary */}
            <div className="bg-white rounded-2xl p-6 shadow-sm h-fit">
              <h3 className="text-[#0a0a0a] font-black italic text-lg mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>comanda ta</h3>
              {cartItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 py-3 border-b border-gray-50">
                  <div className="w-12 h-12 bg-[#0a0a0a] rounded-lg flex items-center justify-center flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0a0a0a] font-black italic text-xs truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>{item.name}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>x{item.quantity}</p>
                  </div>
                  <span className="text-[#0a0a0a] font-black text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {appliedPromo && (
                <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-50">
                  <span className="text-green-600 font-bold text-xs flex items-center gap-1" style={{ fontFamily: 'Nunito, sans-serif' }}><Tag size={12} /> {appliedPromo.code}</span>
                  <span className="text-green-600 font-bold text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
                <span className="text-[#0a0a0a] font-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Total</span>
                <span className="text-[#0a0a0a] font-black text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>${finalTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default CheckoutPage;
